import { PlusOutlined } from '@ant-design/icons'
import { Button, Progress, Segmented, Space, Table, Tag, Typography } from 'antd'
import VSpace from '../../common/VSpace'
import { LongRunningTaskSummary } from './longRunningTasksApi'
import {
  formatDateTime,
  formatProgress,
  getProgressAppearance,
  getProgressPercent,
  listAutoRefreshInterval,
  RefreshSetting,
  resolveRefreshInterval,
  StatusFilter,
  statusFilterOptions,
  taskStatusColor,
} from './longRunningTasksModel'
import { useLongRunningTasksQuery } from './longRunningTasksQueries'
import { getTaskTypeLabel } from './taskTypesModel'
import RefreshControl from './RefreshControl'

type Props = {
  filter: StatusFilter
  onFilterChange: (filter: StatusFilter) => void
  refreshSetting: RefreshSetting
  onRefreshSettingChange: (setting: RefreshSetting) => void
  onSelect: (id: number) => void
  onCreate: () => void
}

const LongRunningTasksList = ({
  filter,
  onFilterChange,
  refreshSetting,
  onRefreshSettingChange,
  onSelect,
  onCreate,
}: Props) => {
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useLongRunningTasksQuery(
    filter,
    latest => resolveRefreshInterval(refreshSetting, listAutoRefreshInterval(latest, Date.now())),
  )
  const autoInterval = listAutoRefreshInterval(data, Date.now())

  const columns = [
    {
      title: 'ID',
      key: 'id',
      width: 80,
      render: (_: unknown, summary: LongRunningTaskSummary) => summary.task.id,
    },
    {
      title: 'Type',
      key: 'type',
      render: (_: unknown, summary: LongRunningTaskSummary) => getTaskTypeLabel(summary.task.taskType),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, summary: LongRunningTaskSummary) => (
        <Space size={4} wrap>
          <Tag color={taskStatusColor(summary.task.status)}>{summary.task.status}</Tag>
          {summary.isStalled && <Tag color="red">Stalled</Tag>}
          {summary.isHeartbeatStale && <Tag color="orange">Heartbeat stale</Tag>}
        </Space>
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_: unknown, summary: LongRunningTaskSummary) => {
        const percent = getProgressPercent(summary.task)
        return (
          <div>
            <Space size={8}>
              <span>{formatProgress(summary.task)}</span>
              {summary.task.failedCount > 0 && (
                <Typography.Text type="danger">{summary.task.failedCount} failed</Typography.Text>
              )}
            </Space>
            {percent !== null && (
              <Progress
                percent={percent}
                size={['100%', 4]}
                showInfo={false}
                style={{ display: 'block', margin: 0, lineHeight: 1, maxWidth: 120 }}
                {...getProgressAppearance(summary.task.status)}
              />
            )}
          </div>
        )
      },
    },
    {
      title: 'Created by',
      key: 'createdBy',
      ellipsis: true,
      render: (_: unknown, summary: LongRunningTaskSummary) => summary.createdByEmail ?? '—',
    },
    {
      title: 'Created',
      key: 'createdAt',
      width: 220,
      render: (_: unknown, summary: LongRunningTaskSummary) => formatDateTime(summary.task.createdAt),
    },
    {
      title: 'Updated',
      key: 'updatedAt',
      width: 220,
      render: (_: unknown, summary: LongRunningTaskSummary) => formatDateTime(summary.task.updatedAt),
    },
  ]

  return (
    <VSpace>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Long-Running Tasks
        </Typography.Title>
        <Space size="large">
          <Segmented<StatusFilter>
            value={filter}
            onChange={onFilterChange}
            options={statusFilterOptions}
          />
          <RefreshControl
            setting={refreshSetting}
            autoInterval={autoInterval}
            isFetching={isFetching}
            dataUpdatedAt={dataUpdatedAt}
            onSettingChange={onRefreshSettingChange}
            onRefresh={() => refetch()}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
            New task
          </Button>
        </Space>
      </div>
      <Table<LongRunningTaskSummary>
        dataSource={data}
        loading={isLoading}
        rowKey={summary => summary.task.id}
        columns={columns}
        onRow={summary => ({
          onClick: () => onSelect(summary.task.id),
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: 'No tasks' }}
        pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
      />
    </VSpace>
  )
}

export default LongRunningTasksList
